import { ArrowBack, Edit, Folder } from "@mui/icons-material";
import { Alert, Box, Button, TextField } from "@mui/material";
import { useGoogleLogin } from "@react-oauth/google";
import ConduccoToDrive from "components/svg/conducco-to-drive";
import GoogleDriveIcon from "components/svg/google-drive-icon";
import WrapperWithHeader from "components/wrapper-with-header";
import { useAlert } from "contexts/alert";
import { IProjectDTO } from "data/dtos/automation/i-project-dto";
import React, { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import api from "services/api";
import ChipStatus from "utils/project/chip-status";
import { useStyles } from "./styles";

interface IProjectConnectGoogleDriveProps {
  keyName: string;
  project: IProjectDTO;
  refreshData: () => Promise<any>;
}

const ProjectConnectGoogleDrive: React.FC<IProjectConnectGoogleDriveProps> = ({
  keyName,
  project,
  refreshData,
}) => {
  const [currentFolderLink, setCurrentFolderLink] = useState<string>("");
  const [isLoadingGoogle, setIsLoadingGoogle] = useState<boolean>(false);
  const [folderIsEditable, setFolderIsEditable] = useState<boolean>(false);

  const { openAlert } = useAlert();
  const history = useHistory();
  const classes = useStyles();

  const login = useGoogleLogin({
    scope: "https://www.googleapis.com/auth/drive",
    flow: "auth-code",
    onSuccess: async (tokenResponse) => {
      try {
        await api.post("organizations/google/auth/callback", {
          code: tokenResponse?.code,
        });

        await updateData();
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoadingGoogle(false);
      }
    },
    onError: () => {
      setIsLoadingGoogle(false);
      openAlert("Algo deu Errado tente novamente mais tarde!", "error");
    },
    onNonOAuthError: () => setIsLoadingGoogle(false),
  });

  const googleDriveConnector = project.connectors.find(
    (connector) => connector.key === keyName
  );

  const successTextList = ["Finalizado", "success", "granted", "authorized"];

  const updateData = async () =>
    await Promise.all([refreshData(), loadGoogleFolderUrl()]);

  const handleBackPage = () => history.push(`/projects/${project.id}`);

  const handleChangeFolder = async (folderLink: string) => {
    try {
      setIsLoadingGoogle(true);
      await api.post("organizations/google/change-folder", {
        folderUrl: folderLink,
      });

      await updateData();
      setFolderIsEditable(false);
      openAlert("Endereço modificado com sucesso", "success");
    } catch (error) {
      openAlert("Algo deu errado ao mudar endereço", "error");
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  const handleRevogeAccess = async () => {
    try {
      setIsLoadingGoogle(true);
      await api.put("organizations/google/revoke");
      await updateData();
      openAlert("Acesso revogado com sucesso", "success");
    } catch (error) {
      openAlert("Algo deu errado ao revogar o acesso", "error");
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  const loadGoogleFolderUrl = async () => {
    try {
      const { data } = await api.get("organizations/google/folder");
      setCurrentFolderLink(data.folderId);
    } catch (error) {
      openAlert("Algo deu errado!", "error");
    }
  };

  useEffect(() => {
    loadGoogleFolderUrl();
  }, []);

  const isSuccess = successTextList.includes(googleDriveConnector!.status);

  return (
    <WrapperWithHeader>
      <Box className={classes.root}>
        <Box className={classes.headerContainer}>
          <Box className={classes.headerText}>
            <h1>
              <ArrowBack
                fontSize="small"
                color="primary"
                onClick={handleBackPage}
                sx={{ cursor: "pointer" }}
              />
              <span> Conectores: </span>
              <span>{googleDriveConnector!.name}</span>
            </h1>

            <div>
              <h5>Status</h5>
              <ChipStatus
                text={googleDriveConnector!.status as string}
                messages={{
                  failed: "Erro na Conexão",
                  success: "Conectado",
                  processing: "Processando",
                  waiting: "Não Conectado",
                }}
              />
            </div>
          </Box>
        </Box>

        <Box className={classes.title}>
          <GoogleDriveIcon />
          <h2>Conecte-se ao Google Drive</h2>
        </Box>

        {!isSuccess && (
          <Box className={classes.content}>
            <ConduccoToDrive />

            <p>Autorizar conexão</p>

            <span>
              Conducco requer acesso limitado à sua conta do Google Drive. Suas
              credenciais serão criptografadas e a autorização poderá ser
              revogada a qualquer momento
            </span>

            <Button
              variant="outlined"
              type="button"
              className={classes.btn}
              onClick={() => {
                login();
                setIsLoadingGoogle(true);
              }}
              disabled={isLoadingGoogle}
            >
              <>
                <GoogleDriveIcon />
                <span>Clique aqui para fazer login</span>
              </>
            </Button>
          </Box>
        )}

        {isSuccess && (
          <Box className={classes.contentConnect}>
            <Alert
              variant="outlined"
              severity="success"
              className={classes.alert}
            >
              <h6>Autorizado com sucesso</h6>
              <span>
                Sua conta Google Drive agora está conectado com a Conducco
              </span>
            </Alert>

            <Box className={classes.info}>
              <h6>
                Agora precisamos que copie e cole o link da pasta que deseja que
                o arquivo seja salvo
              </h6>

              <span>
                {`Você pode encontrar o link da pasta em sua conta do Google Drive clicando com o botão direito na pasta que deseja sincronizar e selecionando Compartilhar > Copia Link`}
              </span>
            </Box>

            {!folderIsEditable ? (
              <TextField
                label="Link da pasta"
                type="text"
                size="medium"
                InputProps={{
                  startAdornment: (
                    <Folder sx={{ marginRight: "0.5rem", opacity: 0.65 }} />
                  ),
                  endAdornment: (
                    <Edit
                      sx={{ cursor: "pointer", opacity: 0.65 }}
                      onClick={() => setFolderIsEditable(true)}
                    />
                  ),
                }}
                value={`https://drive.google.com/drive/u/0/folders/${currentFolderLink}`}
                disabled={true}
              />
            ) : (
              <TextField
                label="Link da pasta"
                type="text"
                size="medium"
                InputProps={{
                  startAdornment: (
                    <Folder sx={{ marginRight: "0.5rem", opacity: 0.65 }} />
                  ),
                }}
                value={currentFolderLink}
                disabled={isLoadingGoogle}
                onChange={(e) => {
                  setCurrentFolderLink(e.target.value);
                }}
              />
            )}

            <Box className={classes.btns}>
              <Button
                type="button"
                variant="contained"
                className={classes.btn}
                onClick={() => handleChangeFolder(currentFolderLink)}
                disabled={isLoadingGoogle}
              >
                Mudar Url
              </Button>

              <Button
                type="button"
                variant="text"
                color="error"
                className={classes.btn}
                onClick={handleRevogeAccess}
                disabled={isLoadingGoogle}
              >
                Revogar acesso
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </WrapperWithHeader>
  );
};

export default ProjectConnectGoogleDrive;
